import React, { createContext, useContext, useState, useCallback } from 'react';
import * as wikiApi from '../api/wikiApi';

/** Рекурсивно сортирует дерево категорий по полю order (на каждом уровне). */
function sortCategoriesTree(tree) {
  if (!Array.isArray(tree)) return [];
  return [...tree]
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map((node) => ({
      ...node,
      children: sortCategoriesTree(node.children || []),
    }));
}

const WikiContext = createContext();

/** Рекурсивно разворачивает дерево категорий в плоский список (сохраняет порядок). */
export function flattenCategoriesTree(tree) {
  if (!Array.isArray(tree)) return [];
  return tree.flatMap((c) => [c, ...flattenCategoriesTree(c.children || [])]);
}

/** В дереве находит родителя категории с id categoryId. */
export function findCategoryParent(tree, categoryId) {
  if (!Array.isArray(tree)) return null;
  const idStr = String(categoryId);
  for (const node of tree) {
    const children = node.children || [];
    if (children.some((ch) => String(ch.id) === idStr)) return node;
    const found = findCategoryParent(children, categoryId);
    if (found) return found;
  }
  return null;
}

/** Собирает id категории и всех её потомков (для исключения из выбора родителя). */
export function getCategoryDescendantIds(cat) {
  const ids = [cat?.id].filter(Boolean);
  (cat?.children || []).forEach((ch) => ids.push(...getCategoryDescendantIds(ch)));
  return ids;
}

/** Ищет узел категории по id в дереве. */
export function findCategoryInTree(tree, categoryId) {
  if (!Array.isArray(tree)) return null;
  const idStr = String(categoryId);
  for (const node of tree) {
    if (String(node.id) === idStr) return node;
    const found = findCategoryInTree(node.children || [], categoryId);
    if (found) return found;
  }
  return null;
}

/** Возвращает id всех предков категории (от корня до родителя), чтобы раскрыть путь в дереве. */
export function getCategoryAncestorIds(tree, categoryId) {
  if (!Array.isArray(tree) || categoryId == null) return [];
  const ids = [];
  let currentId = categoryId;
  let parent = findCategoryParent(tree, currentId);
  while (parent) {
    ids.push(parent.id);
    currentId = parent.id;
    parent = findCategoryParent(tree, currentId);
  }
  return ids;
}

/** Строит дерево из плоского списка с parentId (если бэк вернул flat). */
function buildTreeFromFlat(flat) {
  if (!Array.isArray(flat) || flat.length === 0) return [];
  const hasNested = flat.some((c) => Array.isArray(c.children) && c.children.length > 0);
  if (hasNested) return flat;
  const byId = new Map();
  flat.forEach((c) => byId.set(String(c.id), { ...c, children: [] }));
  const roots = [];
  flat.forEach((c) => {
    const node = byId.get(String(c.id));
    const parentId = c.parentId ?? c.parent_id ?? null;
    if (parentId == null || !byId.has(String(parentId))) roots.push(node);
    else byId.get(String(parentId)).children.push(node);
  });
  return roots;
}

/** Присваивает order по индексу на каждом уровне, учитывая displayOrder с бэка. */
function ensureOrderInTree(tree) {
  if (!Array.isArray(tree)) return tree;
  return tree.map((node, i) => ({
    ...node,
    order:
      node.displayOrder != null
        ? node.displayOrder
        : node.order != null
          ? node.order
          : i,
    children: ensureOrderInTree(node.children || []),
  }));
}


export const useWiki = () => {
  const ctx = useContext(WikiContext);
  if (!ctx) throw new Error('useWiki must be used within WikiProvider');
  return ctx;
};

export const WikiProvider = ({ children }) => {
  const [pages, setPages] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const normalizePage = useCallback((p) => {
    const authorSource =
      p.updatedBy ?? p.lastModifiedBy ?? p.createdBy ?? p.author;
    const authorStr =
      authorSource == null
        ? ''
        : typeof authorSource === 'object'
          ? authorSource.displayName ?? authorSource.login ?? authorSource.name ?? ''
          : String(authorSource);
    const category =
      p.category != null && typeof p.category === 'object'
        ? p.category
        : p.categoryId != null
          ? { id: p.categoryId, name: '', slug: '' }
          : undefined;
    return {
      ...p,
      content: p.contentHtml ?? p.content,
      category,
      author: authorStr || p.author,
    };
  }, []);

  const loadPages = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await wikiApi.getPages({ size: 500 });
      const list = Array.isArray(data) ? data : data?.content ?? data?.pages ?? [];
      setPages(list.map(normalizePage));
    } catch (err) {
      setError(err.message);
      setPages([]);
    } finally {
      setLoading(false);
    }
  }, [normalizePage]);

  const loadCategories = useCallback(async () => {
    try {
      const data = await wikiApi.getCategories();
      const raw = Array.isArray(data) ? data : data?.content ?? [];
      const tree = buildTreeFromFlat(raw);
      const withOrder = ensureOrderInTree(tree);
      setCategories(withOrder);
    } catch (_) {
      setCategories([]);
    }
  }, []);

  const fetchPageBySlug = useCallback(async (slug) => {
    try {
      const page = await wikiApi.getPageBySlug(slug);
      if (page) {
        const normalized = normalizePage(page);
        setPages((prev) => (prev.some((p) => p.slug === slug) ? prev : [...prev, normalized]));
        return normalized;
      }
      return null;
    } catch (_) {
      return null;
    }
  }, [normalizePage]);

  const getPage = useCallback((slug) => {
    return pages.find((p) => p.slug === slug) ?? null;
  }, [pages]);

  const getPageById = useCallback((id) => {
    return pages.find((p) => p.id === id || String(p.id) === String(id)) ?? null;
  }, [pages]);

  const getAllPages = useCallback(() => pages, [pages]);

  const getCategories = useCallback(() => {
    const flat = flattenCategoriesTree(categories);
    return [...flat].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [categories]);

  const getCategoriesTree = useCallback(() => sortCategoriesTree(categories), [categories]);

  const incrementViews = useCallback(async (id) => {
    try {
      await wikiApi.incrementViews(id);
      const idStr = String(id);
      setPages((prev) =>
        prev.map((p) => (String(p.id) === idStr ? { ...p, views: (p.views || 0) + 1 } : p))
      );
    } catch (_) {}
  }, []);

  const createPage = useCallback(async (data) => {
    const payload = {
      ...data,
      categoryId: Number(data.category) || data.category,
      content: data.content ?? data.contentHtml,
    };
    const created = await wikiApi.createPage(payload);
    setPages((prev) => [...prev, normalizePage(created)].filter(Boolean));
    return normalizePage(created);
  }, [normalizePage]);

  const updatePage = useCallback(async (id, data) => {
    const payload = {
      ...data,
      categoryId: Number(data.category) || data.category,
      content: data.content ?? data.contentHtml,
    };
    const updated = await wikiApi.updatePage(id, payload);
    const idStr = String(id);
    setPages((prev) =>
      prev.map((p) => (String(p.id) === idStr ? normalizePage({ ...p, ...updated }) : p))
    );
    return normalizePage(updated);
  }, [normalizePage]);

  const deletePage = useCallback(async (id) => {
    await wikiApi.deletePage(id);
    const idStr = String(id);
    setPages((prev) => prev.filter((p) => String(p.id) !== idStr));
  }, []);

  const addCategory = useCallback(
    async (data) => {
      const created = await wikiApi.addCategory(data);
      await loadCategories();
      return created;
    },
    [loadCategories]
  );

  const updateCategory = useCallback(
    async (id, data) => {
      const updated = await wikiApi.updateCategory(id, data);
      await loadCategories();
      return updated;
    },
    [loadCategories]
  );

  const deleteCategory = useCallback(
    async (id) => {
      await wikiApi.deleteCategory(id);
      await loadCategories();
    },
    [loadCategories]
  );

  const getHistory = useCallback(async (filters = {}) => {
    const data = await wikiApi.getHistory({ page: filters.page ?? 0, size: filters.size ?? 100 });
    const raw = Array.isArray(data)
      ? data
      : data?.content ?? data?.Content ?? [];
    return (raw || []).map((entry) => {
      if (!entry || typeof entry !== 'object') return null;
      const pageId = entry.page?.id ?? entry.pageId;
      const pageTitle = entry.page?.title ?? entry.pageTitle;
      const author =
        typeof entry.author === 'object' && entry.author != null
          ? entry.author.displayName ?? entry.author.login ?? ''
          : String(entry.author ?? '');
      const action = entry.action != null ? String(entry.action).toLowerCase() : '';
      return {
        ...entry,
        pageId,
        pageTitle,
        author,
        action: action || entry.action,
      };
    }).filter(Boolean);
  }, []);

  const getVersions = useCallback(async (pageId) => {
    const data = await wikiApi.getVersions(pageId);
    return Array.isArray(data) ? data : data?.content ?? [];
  }, []);

  const restoreVersion = useCallback(async (pageId, versionId, author) => {
    const updated = await wikiApi.restoreVersion(pageId, versionId, author);
    const pageIdStr = String(pageId);
    setPages((prev) =>
      prev.map((p) => (String(p.id) === pageIdStr ? { ...p, ...updated } : p))
    );
    return updated;
  }, []);

  const searchPages = useCallback((query) => {
    if (!query?.trim()) return pages;
    const q = query.toLowerCase().trim();
    return pages.filter(
      (p) =>
        (p.title && p.title.toLowerCase().includes(q)) ||
        ((p.content || p.contentHtml) && (p.content || p.contentHtml).toLowerCase().includes(q))
    );
  }, [pages]);

  const value = {
    pages,
    categories,
    loading,
    error,
    loadPages,
    loadCategories,
    fetchPageBySlug,
    getPage,
    getPageById,
    getAllPages,
    getCategories,
    getCategoriesTree,
    incrementViews,
    createPage,
    updatePage,
    deletePage,
    addCategory,
    updateCategory,
    deleteCategory,
    getHistory,
    getVersions,
    restoreVersion,
    searchPages,
  };

  return <WikiContext.Provider value={value}>{children}</WikiContext.Provider>;
};
